import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChecklistIcon from "@mui/icons-material/Checklist";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { alpha, Box, Chip, Stack, Typography, useTheme } from "@mui/material";

type FormProgressChipsProps = {
    completedSections: number;
    totalSections: number;
    completedFields?: number | null;
    totalFields?: number | null;
    remainingSeconds?: number | null;
};

function formatEta(seconds?: number | null) {
    if (seconds === null || seconds === undefined) return "—";

    const s = Math.max(0, Math.round(seconds));
    const m = Math.floor(s / 60);
    const h = Math.floor(m / 60);
    const mm = m % 60;

    if (h > 0) return `${h}h ${mm}m left`;
    if (m > 0) return `${m}m left`;
    return `${s}s left`;
}

export function FormProgressChips({
    completedSections,
    totalSections,
    completedFields,
    totalFields,
    remainingSeconds,
}: FormProgressChipsProps) {
    const theme = useTheme();

    const sectionPercent =
        totalSections > 0 ? Math.round((completedSections / totalSections) * 100) : 0;

    const fieldPercent =
        totalFields && totalFields > 0 && completedFields !== null && completedFields !== undefined
            ? Math.round((completedFields / totalFields) * 100)
            : null;

    return (
        <Box
            sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                bgcolor: "background.paper",
                py: 1.25,
                mb: 1.5,
                borderBottom: "1px solid rgba(0,0,0,0.08)",
            }}
        >
            <Stack spacing={1}>

                <Stack direction="row" spacing={1.2} useFlexGap flexWrap="wrap">
                    {/* Time */}
                    <Chip
                        icon={<AccessTimeIcon />}
                        label={`Time left: ${formatEta(remainingSeconds)}`}
                        sx={{
                            borderRadius: 999,
                            fontWeight: 600,
                            bgcolor: alpha(theme.palette.warning.main, 0.12),
                            color: theme.palette.warning.dark,
                            "& .MuiChip-icon": {
                                color: theme.palette.warning.main,
                            },
                        }}
                    />

                    {/* Fields */}
                    <Chip
                        icon={<ChecklistIcon />}
                        label={`Fields: ${completedFields ?? "—"} / ${totalFields ?? "—"}`}
                        sx={{
                            borderRadius: 999,
                            fontWeight: 500,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.dark,
                            "& .MuiChip-icon": {
                                color: theme.palette.primary.main,
                            },
                        }}
                    />

                    {/* Sections */}
                    <Chip
                        icon={<FolderOpenIcon />}
                        label={`Sections: ${completedSections} / ${totalSections}`}
                        sx={{
                            borderRadius: 999,
                            fontWeight: 500,
                            bgcolor: alpha(theme.palette.info.main, 0.12),
                            color: theme.palette.info.dark,
                            "& .MuiChip-icon": {
                                color: theme.palette.info.main,
                            },
                        }}
                    />

                    {/* Completion */}
                    <Chip
                        icon={<TrendingUpIcon />}
                        label={`Complete: ${fieldPercent ?? sectionPercent}%`}
                        sx={{
                            borderRadius: 999,
                            fontWeight: 600,
                            bgcolor: alpha(theme.palette.success.main, 0.12),
                            color: theme.palette.success.dark,
                            "& .MuiChip-icon": {
                                color: theme.palette.success.main,
                            },
                        }}
                    />
                </Stack>
            </Stack>
        </Box>
    );
}